import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import {
  CalendarMonth,
  ViewWeek,
  Today,
} from '@mui/icons-material';
import { Calendar, dateFnsLocalizer, View, SlotInfo } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addDays, startOfMonth, endOfMonth, startOfDay, endOfDay } from 'date-fns';
import { fr } from 'date-fns/locale/fr';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { Club, Court } from '../types';

const locales = {
  'fr': fr,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

interface CalendarEvent {
  id?: number;
  title: string;
  start: Date;
  end: Date;
  resource: {
    slot: any;
    court: Court | null;
    club: Club | null;
    status: string;
  };
}

export const CalendarPage: React.FC = () => {
  const { user } = useAuth();
  const [currentView, setCurrentView] = useState<View>('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [clubs, setClubs] = useState<Club[]>([]);
  const [courts, setCourts] = useState<Court[]>([]);
  const [selectedClubId, setSelectedClubId] = useState<number | ''>('');
  const [selectedCourtId, setSelectedCourtId] = useState<number | ''>('');
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);

  useEffect(() => {
    loadClubs();
  }, []);

  useEffect(() => {
    if (selectedClubId) {
      loadCourts(selectedClubId);
    } else {
      setCourts([]);
      setSelectedCourtId('');
    }
  }, [selectedClubId]);

  useEffect(() => {
    if (selectedClubId || selectedCourtId) {
      loadTimeSlots();
    } else {
      setEvents([]);
    }
  }, [selectedClubId, selectedCourtId, currentView, currentDate]);

  const loadClubs = async () => {
    try {
      const response = await apiService.getClubs();
      const data: any = response.data;
      const clubsData = Array.isArray(data) ? data : (data?.clubs || data?.data || []);
      setClubs(clubsData.filter((club: Club) => club.is_active));
    } catch (err: any) {
      console.error('Error loading clubs:', err);
      setError('Failed to load clubs');
    }
  };

  const loadCourts = async (clubId: number) => {
    try {
      const response = await apiService.getAllCourts();
      const data: any = response.data;
      const courtsData = Array.isArray(data) ? data : (data?.courts || data?.data || []);
      setCourts(courtsData.filter((court: Court) => court.club_id === clubId && court.is_active));
    } catch (err: any) {
      console.error('Error loading courts:', err);
      setError('Failed to load courts');
    }
  };

  const getDateRange = useCallback(() => {
    let start: Date;
    let end: Date;

    switch (currentView) {
      case 'month':
        start = startOfMonth(currentDate);
        end = endOfMonth(currentDate);
        break;
      case 'week':
        start = startOfWeek(currentDate, { weekStartsOn: 1 });
        end = addDays(start, 6);
        break;
      case 'day':
        start = startOfDay(currentDate);
        end = endOfDay(currentDate);
        break;
      default:
        start = startOfMonth(currentDate);
        end = endOfMonth(currentDate);
    }

    return { start, end };
  }, [currentView, currentDate]);

  const loadTimeSlots = async () => {
    if (!selectedClubId && !selectedCourtId) return;

    try {
      setLoading(true);
      setError(null);

      const { start, end } = getDateRange();
      const dateFrom = format(start, 'yyyy-MM-dd');
      const dateTo = format(end, 'yyyy-MM-dd');

      let slots: any[] = [];

      if (selectedCourtId) {
        // Fetch slots for specific court
        const response = await apiService.getTimeSlots({
          court_id: selectedCourtId as number,
          date_from: dateFrom,
          date_to: dateTo,
          include_booked: true,
        });
        slots = Array.isArray(response.data) ? response.data : [];
      } else if (selectedClubId) {
        // Fetch slots for all courts in the club
        const response = await apiService.getTimeSlotsByClub(selectedClubId as number, {
          date_from: dateFrom,
          date_to: dateTo,
          include_booked: true,
        });
        slots = Array.isArray(response.data) ? response.data : [];
      }

      // Transform slots to calendar events
      const calendarEvents: CalendarEvent[] = [];
      const courtsMap = new Map(courts.map(c => [c.id, c]));
      const clubsMap = new Map(clubs.map(c => [c.id, c]));

      for (const slot of slots) {
        const court = courtsMap.get(slot.court_id) || null;
        const club = court ? clubsMap.get(court.club_id) || null : null;

        // Handle date format - could be string or Date object
        const slotDate = typeof slot.date === 'string' ? new Date(slot.date) : new Date(slot.date);
        
        // Handle time format - could be HH:mm or HH:mm:ss
        const startTimeStr = slot.start_time || slot.startTime || '';
        const endTimeStr = slot.end_time || slot.endTime || '';
        
        if (!startTimeStr || !endTimeStr) continue; // Skip invalid slots
        
        const [startHours, startMinutes] = startTimeStr.split(':').map(Number);
        const [endHours, endMinutes] = endTimeStr.split(':').map(Number);

        if (isNaN(startHours) || isNaN(startMinutes) || isNaN(endHours) || isNaN(endMinutes)) {
          continue; // Skip invalid time slots
        }

        const startDateTime = new Date(slotDate);
        startDateTime.setHours(startHours, startMinutes, 0, 0);

        const endDateTime = new Date(slotDate);
        endDateTime.setHours(endHours, endMinutes, 0, 0);

        const status = slot.status || 'AVAILABLE';
        const courtName = slot.court_name || court?.name || `Court ${slot.court_id}`;
        const timeRange = `${format(startDateTime, 'HH:mm')} - ${format(endDateTime, 'HH:mm')}`;

        calendarEvents.push({
          id: slot.id,
          title: `${courtName} - ${timeRange}`,
          start: startDateTime,
          end: endDateTime,
          resource: {
            slot,
            court,
            club,
            status,
          },
        });
      }

      setEvents(calendarEvents);
    } catch (err: any) {
      console.error('Error loading time slots:', err);
      setError(err.response?.data?.detail || 'Failed to load time slots');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEvent = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setDialogOpen(true);
  };

  const handleSelectSlot = (slotInfo: SlotInfo) => {
    // Optional: Allow creating new slots by clicking on empty slots
    // For now, we'll just show a message
    console.log('Selected slot:', slotInfo);
  };

  const handleBookSlot = async () => {
    if (!selectedEvent?.id) return;

    try {
      setLoading(true);
      await apiService.bookTimeSlot(selectedEvent.id, 'match');
      setDialogOpen(false);
      setBookingDialogOpen(false);
      await loadTimeSlots();
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to book time slot');
    } finally {
      setLoading(false);
    }
  };

  const handleBlockSlot = async () => {
    if (!selectedEvent?.id || !user?.is_admin) return;

    try {
      setLoading(true);
      await apiService.blockTimeSlot(selectedEvent.id);
      setDialogOpen(false);
      await loadTimeSlots();
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to block time slot');
    } finally {
      setLoading(false);
    }
  };

  const eventStyleGetter = (event: CalendarEvent) => {
    const status = event.resource.status;
    let backgroundColor = '#3174ad'; // Default blue

    switch (status) {
      case 'AVAILABLE':
        backgroundColor = '#4caf50'; // Green
        break;
      case 'BOOKED':
        backgroundColor = '#2196f3'; // Blue
        break;
      case 'MAINTENANCE':
      case 'BLOCKED':
        backgroundColor = '#f44336'; // Red
        break;
      case 'UNAVAILABLE':
        backgroundColor = '#9e9e9e'; // Gray
        break;
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: 0.8,
        color: 'white',
        border: '0px',
        display: 'block',
      },
    };
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom fontWeight={600}>
        Calendrier des créneaux
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Club</InputLabel>
              <Select
                value={selectedClubId}
                onChange={(e) => setSelectedClubId(e.target.value as number | '')}
                label="Club"
              >
                <MenuItem value="">
                  <em>Tous les clubs</em>
                </MenuItem>
                {clubs.map((club) => (
                  <MenuItem key={club.id} value={club.id}>
                    {club.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 200 }} disabled={!selectedClubId}>
              <InputLabel>Court</InputLabel>
              <Select
                value={selectedCourtId}
                onChange={(e) => setSelectedCourtId(e.target.value as number | '')}
                label="Court"
              >
                <MenuItem value="">
                  <em>Tous les courts</em>
                </MenuItem>
                {courts.map((court) => (
                  <MenuItem key={court.id} value={court.id}>
                    {court.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ flexGrow: 1 }} />

            <ToggleButtonGroup
              value={currentView}
              exclusive
              onChange={(_, newView) => newView && setCurrentView(newView)}
              aria-label="view selection"
            >
              <ToggleButton value="month" aria-label="month view">
                <CalendarMonth sx={{ mr: 1 }} />
                Mois
              </ToggleButton>
              <ToggleButton value="week" aria-label="week view">
                <ViewWeek sx={{ mr: 1 }} />
                Semaine
              </ToggleButton>
              <ToggleButton value="day" aria-label="day view">
                <Today sx={{ mr: 1 }} />
                Jour
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Calendar */}
      <Paper sx={{ p: 2, height: 'calc(100vh - 300px)', minHeight: 600 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress />
          </Box>
        ) : (
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            view={currentView}
            onView={setCurrentView}
            date={currentDate}
            onNavigate={setCurrentDate}
            onSelectEvent={handleSelectEvent}
            onSelectSlot={handleSelectSlot}
            selectable
            eventPropGetter={eventStyleGetter}
            messages={{
              next: 'Suivant',
              previous: 'Précédent',
              today: "Aujourd'hui",
              month: 'Mois',
              week: 'Semaine',
              day: 'Jour',
              agenda: 'Agenda',
              date: 'Date',
              time: 'Heure',
              event: 'Événement',
              noEventsInRange: 'Aucun créneau disponible pour cette période',
            }}
          />
        )}
      </Paper>

      {/* Event Details Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Détails du créneau
        </DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Club
                </Typography>
                <Typography variant="body1">
                  {selectedEvent.resource.club?.name || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Court
                </Typography>
                <Typography variant="body1">
                  {selectedEvent.resource.court?.name || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Date et heure
                </Typography>
                <Typography variant="body1">
                  {format(selectedEvent.start, 'dd/MM/yyyy HH:mm')} - {format(selectedEvent.end, 'HH:mm')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Statut
                </Typography>
                <Chip
                  label={selectedEvent.resource.status}
                  color={
                    selectedEvent.resource.status === 'AVAILABLE' ? 'success' :
                    selectedEvent.resource.status === 'BOOKED' ? 'primary' :
                    selectedEvent.resource.status === 'MAINTENANCE' || selectedEvent.resource.status === 'BLOCKED' ? 'error' :
                    'default'
                  }
                  size="small"
                />
              </Box>
              {selectedEvent.resource.slot?.price && (
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Prix
                  </Typography>
                  <Typography variant="body1">
                    {selectedEvent.resource.slot.price} €
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {selectedEvent?.resource.status === 'AVAILABLE' && (
            <Button onClick={() => {
              setBookingDialogOpen(true);
              setDialogOpen(false);
            }} color="primary" variant="contained">
              Réserver
            </Button>
          )}
          {user?.is_admin && selectedEvent?.resource.status === 'AVAILABLE' && (
            <Button onClick={handleBlockSlot} color="error" variant="outlined">
              Bloquer
            </Button>
          )}
          <Button onClick={() => setDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Booking Confirmation Dialog */}
      <Dialog open={bookingDialogOpen} onClose={() => setBookingDialogOpen(false)}>
        <DialogTitle>Confirmer la réservation</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir réserver ce créneau ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBookingDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleBookSlot} color="primary" variant="contained" disabled={loading}>
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

